import type { CheckoutCompletion } from "@pax/api-contract/payments/v1alpha1";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useSession } from "@/features/auth/queries";
import { api, reachable } from "@/lib/api";
import type { CheckoutReturnNoticeKind } from "./components/CheckoutReturnNotice";
import {
  creditBalanceQuery,
  mySubscriptionQuery,
  refreshCredits,
  refreshTopUp,
  spendableCredits,
} from "./queries";

const RETURN_PARAM = "checkout_session_id";
/** Sent only on the tab Stripe was opened in, so that tab can close itself when Stripe returns. */
export const CHECKOUT_CLOSE_PATH = "/?checkout_close=1";
const CLOSE_PARAM = "checkout_close";
const CHECKOUT_STARTED = "pax:checkout-started";
export const AUTO_TOP_UP_SUCCESS = "pax:auto-top-up-success";

/** Lets the top-up picker close and show a success notice from any path that turned auto top-up on. */
export function signalAutoTopUpSuccess() {
  window.dispatchEvent(new CustomEvent(AUTO_TOP_UP_SUCCESS));
}

/** A paid checkout's credits can land a few seconds after the player is back. */
const CREDIT_WAIT_MS = 30_000;
const CREDIT_POLL_MS = 1_500;

type RefusedJump = { guidance: string };

const storageKey = (gameId: string) => `pax:refused-jump:${gameId}`;

/** Remembers the ask whose turn was refused for credits, so paying can ask it again. */
export function rememberRefusedJump(gameId: string, guidance: string) {
  try {
    sessionStorage.setItem(storageKey(gameId), JSON.stringify({ guidance } satisfies RefusedJump));
  } catch {
    // Storage can be unavailable; the player can still ask again by hand.
  }
}

export function forgetRefusedJump(gameId: string) {
  try {
    sessionStorage.removeItem(storageKey(gameId));
  } catch {
    // As above.
  }
}

function refusedJump(gameId: string): RefusedJump | null {
  try {
    const stored = sessionStorage.getItem(storageKey(gameId));
    const parsed: unknown = stored ? JSON.parse(stored) : null;
    return parsed && typeof (parsed as RefusedJump).guidance === "string"
      ? (parsed as RefusedJump)
      : null;
  } catch {
    return null;
  }
}

function returnedFromCheckout(): boolean {
  return new URL(window.location.href).searchParams.has(RETURN_PARAM);
}

/**
 * Checkout opened in another tab, so this page stays put and watches for the credits to land.
 * The balance is the one showing before the purchase, and a different one means it arrived.
 */
export function markCheckoutStarted(balance: number | null) {
  window.dispatchEvent(new CustomEvent(CHECKOUT_STARTED, { detail: { balance } }));
}

/** Opens Stripe in a tab created during the click, before the address comes back. */
export function openStripeTab(): Window | null {
  const tab = window.open("about:blank", "_blank");
  if (tab) tab.opener = null;
  return tab;
}

export function sendToStripe(tab: Window | null, url: string) {
  if (tab && !tab.closed) tab.location.href = url;
  else window.location.assign(url);
}

/**
 * Back from a paid checkout on a game whose turn was refused for credits, asks for that turn
 * again once the credits have landed.
 */
export function useResumeAfterCheckout({
  gameId,
  ready,
  requestJump,
}: {
  gameId: string;
  ready: boolean;
  requestJump: (guidance: string) => Promise<unknown>;
}) {
  const [pending, setPending] = useState(() =>
    returnedFromCheckout() ? refusedJump(gameId) : null,
  );
  const [since, setSince] = useState(() => Date.now());
  const asked = useRef(false);

  // A purchase from this page opens Stripe in another tab, so the ask is retried here.
  useEffect(() => {
    const start = () => {
      const jump = refusedJump(gameId);
      if (!jump) return;
      asked.current = false;
      setSince(Date.now());
      setPending(jump);
    };
    window.addEventListener(CHECKOUT_STARTED, start);
    return () => window.removeEventListener(CHECKOUT_STARTED, start);
  }, [gameId]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(RETURN_PARAM)) return;
    url.searchParams.delete(RETURN_PARAM);
    window.history.replaceState(window.history.state, "", url);
  }, []);

  const userId = useSession().data?.user.id;
  const { data: credits } = useQuery({
    ...creditBalanceQuery(userId ?? ""),
    enabled: pending !== null && userId !== undefined,
    refetchInterval: query =>
      (query.state.data ? spendableCredits(query.state.data) : 0) > 0 ||
      Date.now() - since > CREDIT_WAIT_MS
        ? false
        : CREDIT_POLL_MS,
  });

  useEffect(() => {
    if (!pending || !ready || asked.current || credits === undefined || credits <= 0) return;
    asked.current = true;
    forgetRefusedJump(gameId);
    void requestJump(pending.guidance).catch(() => undefined);
  }, [pending, ready, credits, gameId, requestJump]);
}

/** Grants a paid pack now rather than when the webhook arrives. A failure leaves it to the webhook. */
async function completeCheckout(
  checkoutSessionId: string,
): Promise<CheckoutCompletion["status"] | null> {
  try {
    const completion = await reachable(
      api.payments.checkoutSessions.complete({ params: { checkoutSessionId } }),
    );
    return completion.status;
  } catch {
    return null;
  }
}

function checkoutReturnNotice(
  status: CheckoutCompletion["status"] | null,
): CheckoutReturnNoticeKind {
  if (status === null) return "unconfirmed";
  switch (status) {
    case "granted":
    case "subscribed":
      return "paid";
    case "processing":
      return "processing";
    case "payment_failed":
      return "payment_failed";
    case "expired":
      return "expired";
    case "not_paid":
      return "cancelled";
    case "auto_top_up_enabled":
      return "auto_top_up_enabled";
    case "auto_top_up_pending":
      return "auto_top_up_pending";
    case "auto_top_up_refused":
      return "auto_top_up_refused";
  }
}

/** Refreshes the balance shown on this page once a purchase in another tab lands. */
export function useCheckoutBalanceWatch() {
  const queryClient = useQueryClient();
  const [watch, setWatch] = useState<{ at: number; balance: number | null } | null>(null);
  const [returned, setReturned] = useState<CheckoutReturnNoticeKind | null>(null);
  // Read while rendering, before a page's own effects strip the parameter.
  const [checkoutSessionId] = useState(() =>
    new URL(window.location.href).searchParams.get(RETURN_PARAM),
  );

  useEffect(() => {
    const url = new URL(window.location.href);
    const closeTab = url.searchParams.get(CLOSE_PARAM) === "1";
    if (!closeTab && !checkoutSessionId) return;
    url.searchParams.delete(RETURN_PARAM);
    url.searchParams.delete(CLOSE_PARAM);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
    void (async () => {
      if (checkoutSessionId) {
        const status = await completeCheckout(checkoutSessionId);
        await Promise.all([
          refreshCredits(queryClient),
          queryClient.invalidateQueries({ queryKey: mySubscriptionQuery.queryKey }),
          refreshTopUp(queryClient),
        ]);
        if (status === "auto_top_up_enabled") signalAutoTopUpSuccess();
        if (status === "granted" || status === "subscribed") {
          if (!closeTab) return;
          window.close();
        }
        setReturned(checkoutReturnNotice(status));
        return;
      }
      if (!closeTab) return;
      window.close();
      setReturned("cancelled");
    })();
  }, [checkoutSessionId, queryClient]);

  useEffect(() => {
    const start = (event: Event) => {
      const balance = (event as CustomEvent<{ balance: number | null }>).detail.balance;
      setWatch({ at: Date.now(), balance });
    };
    window.addEventListener(CHECKOUT_STARTED, start);
    return () => window.removeEventListener(CHECKOUT_STARTED, start);
  }, []);

  useEffect(() => {
    if (!watch) return;
    const timer = setTimeout(() => setWatch(null), CREDIT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [watch]);

  const userId = useSession().data?.user.id;
  const { data } = useQuery({
    ...creditBalanceQuery(userId ?? ""),
    enabled: watch !== null && userId !== undefined,
    refetchInterval: () =>
      watch && Date.now() - watch.at < CREDIT_WAIT_MS ? CREDIT_POLL_MS : false,
  });

  useEffect(() => {
    if (!watch || data === undefined || data === watch.balance) return;
    void refreshCredits(queryClient);
    void queryClient.invalidateQueries({ queryKey: mySubscriptionQuery.queryKey });
    void refreshTopUp(queryClient);
    setWatch(null);
  }, [watch, data, queryClient]);

  return returned;
}
